import React, { useEffect, useRef, useState } from 'react';

import styles from './NamePromptModal.module.css';

export interface NamePromptModalProps {
  open: boolean;
  title: string;
  label: string;
  initialValue: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}

export const NamePromptModal: React.FC<NamePromptModalProps> = ({
  open,
  title,
  label,
  initialValue,
  confirmLabel = 'OK',
  onCancel,
  onConfirm,
}) => {
  const [value, setValue] = useState<string>(initialValue);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setValue(initialValue);
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const trimmed = value.trim();
  const canSubmit = trimmed !== '';

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    onConfirm(trimmed);
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="name-prompt-modal"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="name-prompt-modal-title"
      >
        <form onSubmit={handleSubmit}>
          <h2 id="name-prompt-modal-title" className={styles.title}>
            {title}
          </h2>
          <label className={styles.label} htmlFor="name-prompt-modal-input">
            {label}
          </label>
          <input
            id="name-prompt-modal-input"
            ref={inputRef}
            className={styles.input}
            type="text"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-testid="name-prompt-modal-input"
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onCancel}
              data-testid="name-prompt-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={!canSubmit}
              data-testid="name-prompt-modal-confirm"
            >
              {confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
